/* eslint-disable @typescript-eslint/ban-ts-comment */

import { useEffect } from 'react';
import type { ReactNode } from 'react';
import type { RedirectHref } from '@zg-rentals/auth-react/client';
import { logError } from '@zg-rentals/log-error';
import { getGlobalMonitor } from '@zg-rentals/monitor-browser';
import type { PopupLoginOptions, RedirectLoginOptions } from '@zillow/auth-core';

// @ts-ignore: local package uses declaration files, ignore for external consumers
import { getZillowWebLoginURL } from '@zillow/auth-core/dist/es/zoam-web-sdk/utils/getZillowWebLoginURL';
// @ts-ignore: local package uses declaration files, ignore for external consumers
import zillowWebLogin from '@zillow/auth-core/dist/es/zoam-web-sdk/core/zillowWebLogin';
import type { ZillowLoginBaseOptions } from '.';

export function resolveAuthBaseUrl(raw = '/') {
  if (!raw.startsWith('/') || typeof window === 'undefined') return raw;
  return raw === '/' ? window.location.origin : `${window.location.origin}${raw}`;
}

export function partialUrlEncode(str: string) {
  return str.replaceAll('%', '%25').replaceAll('&', '%26').replaceAll('?', '%3F').replaceAll('#', '%23');
}

export type ZillowRedirectLoginOptions = ZillowLoginBaseOptions &
  Omit<RedirectLoginOptions, 'loginMode' | 'trackingConfig' | 'envConfig' | 'redirectUri'> & {
    /** Base URL for the login flow (defaults to https://www.zillow.com if undefined) */
    baseURL?: string;

    /** Optional: path to redirect to after login (defaults to location.pathname) */
    redirectPath?: string;

    /**
     * Optional: forward prefix, use for local development to support redirecting back to localhost dev server
     * @note only works for serverless apps; SSR apps still need to use `rjs-ext` to login and copy cookies to localhost
     */
    forwardPrefix?: string;
  };

/**
 * Returns a Zillow login URL string for use in client-rendered anchor `href`
 * values.
 *
 * @note `forwardPrefix` only supported for serverless apps
 *
 * @example
 * ```typescript
 * <a href={zillowLoginHref({ entryPoint: 'example-web', baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL })}>
 *   Login
 * </a>
 * ```
 */
export function zillowLoginHref(
  { baseURL, disableForceLogin, forwardPrefix, redirectPath, ...options }: ZillowRedirectLoginOptions,
  params = new URLSearchParams(),
): string {
  const resolvedBaseURL = resolveAuthBaseUrl(baseURL);
  const resolvedRedirectPath = redirectPath ?? `${location.pathname}?${params}`;

  return getZillowWebLoginURL({
    ...options,
    baseURL: resolvedBaseURL,
    forceLogin: !disableForceLogin,
    redirectUri: forwardPrefix
      ? `${forwardPrefix}?protocol=https${location.port ? `&port=${location.port}` : ''}&path=${partialUrlEncode(
          resolvedRedirectPath,
        )}`
      : resolvedRedirectPath,
  });
}

/**
 * Returns a `redirectHref` callback for use with `Auth.Boundary` on the
 * client. The callback builds a Zillow login URL from the current page path
 * and auth query params.
 *
 * @note `forwardPrefix` only supported for serverless apps
 *
 * @example
 * ```typescript
 * <Auth.Boundary redirectHref={zillowLoginRedirectHref({ entryPoint: 'example-web', baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL })}>
 *   <p>Hello logged in user</p>
 * </Auth.Boundary>
 * ```
 */
export function zillowLoginRedirectHref(options: ZillowRedirectLoginOptions): RedirectHref {
  return (_href: string, params: URLSearchParams) => zillowLoginHref(options, params);
}

/**
 * Triggers a Zillow web login via full-page redirect. Use this for standalone
 * login buttons outside of an `Auth.Boundary`.
 *
 * @note use this function in an `onClick` handler or `useEffect` directly since it produces side-effects
 * @note `forwardPrefix` only supported for serverless apps
 *
 * @example
 * ```typescript
 * <button onClick={() => zillowRedirectLogin({ baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL, entryPoint: 'example-web' })}>
 *   Login
 * </button>
 * ```
 *
 * @example
 * ```typescript
 * useEffect(() => {
 *   zillowRedirectLogin({ baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL, entryPoint: 'example-web' });
 * }, []);
 * ```
 */
export async function zillowRedirectLogin({
  baseURL,
  disableForceLogin,
  entryPoint,
  forwardPrefix,
  redirectPath,
  ...options
}: ZillowRedirectLoginOptions) {
  const resolvedBaseURL = resolveAuthBaseUrl(baseURL);

  redirectPath ||= location.pathname;

  const redirectUri = forwardPrefix
    ? `${forwardPrefix}?protocol=https${
        typeof window !== 'undefined' && window.location.port ? `&port=${window.location.port}` : ''
      }&path=${partialUrlEncode(redirectPath)}`
    : redirectPath;

  return zillowWebLogin({
    ...options,
    forceLogin: !disableForceLogin,
    envConfig: { baseURLOverride: resolvedBaseURL },
    loginMode: 'redirect',
    redirectUri,
    trackingConfig: { entryPoint },
  });
}

export type ZillowPopupLoginOptions = ZillowLoginBaseOptions &
  Omit<PopupLoginOptions, 'loginMode' | 'trackingConfig' | 'envConfig'> & {
    /** Base URL for the login flow (defaults to https://www.zillow.com if undefined) */
    baseURL?: string;
  };

/**
 * Triggers a Zillow web login using popup mode.
 *
 * @note use this function in an `onClick` handler or `useEffect` directly since it produces side-effects
 * @note due to ZOAM limitations this does not work well in local development; use `zillowRedirectLogin` instead (if possible)
 *
 * @example
 * ```typescript
 * <button onClick={() => zillowPopupLogin({ baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL, entryPoint: 'example-web' })}>
 *   Login
 * </button>
 * ```
 *
 * @example
 * ```typescript
 * useEffect(() => {
 *   zillowPopupLogin({ baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL, entryPoint: 'example-web' });
 * }, []);
 * ```
 */
export async function zillowPopupLogin({
  baseURL,
  disableForceLogin,
  entryPoint,
  ...options
}: ZillowPopupLoginOptions) {
  const resolvedBaseURL = resolveAuthBaseUrl(baseURL);

  return zillowWebLogin({
    ...options,
    forceLogin: !disableForceLogin,
    envConfig: { baseURLOverride: resolvedBaseURL },
    loginMode: 'popup',
    trackingConfig: { entryPoint },
  });
}

const DEFAULT_MFA_RETRY_PARAM = 'mr';
const DEFAULT_MAX_MFA_RETRIES = 3;
const ERROR_TYPE_MFA_STEP_UP_RETRY_EXHAUSTED = 'MfaStepUpRetryExhausted';
const ERROR_TYPE_MFA_STEP_UP_ATTEMPT = 'MfaStepUpAttempt';
const SUCCESS_TYPE_MFA_STEP_UP = 'MfaStepUpSucceeded';

// Returns all values for a cookie name joined by ',' — same-name cookies can
// coexist when scoped to different Domain/Path, and surfacing all of them is
// the point of this logging.
function getCookie(name: string) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const matches = document.cookie
    .split('; ')
    .filter((c) => c.startsWith(name + '='))
    .map((c) => c.slice(name.length + 1));

  return matches.length ? matches.join(',') : undefined;
}

type MfaRetryHandlerOptions<U = unknown> = {
  /** App name used to tag the retry-exhausted log so dashboards can split by caller. */
  appName: string;
  /** Build number used to tag the retry-exhausted log so we can attribute the error to a specific deploy. */
  buildNumber?: string | number;
  /** User DTO; retry only fires when `isInitialMultiFactorAuthenticationRequired` is true. */
  user?: (U & { isInitialMultiFactorAuthenticationRequired?: boolean }) | null;
  /** The same `redirectHref` passed to `Auth.Boundary`; invoked to rebuild the login URL with the bumped counter. */
  redirectHref: RedirectHref;
  /** Rendered when retry does not apply (server, MFA not required, or max retries reached). Mirrors the `onUnauthorized` prop shape. */
  fallback: ReactNode | ((user: U) => ReactNode);
  /** Rendered while `window.location.href` is being assigned so React does not flash the fallback. */
  redirecting: ReactNode;
  /** Override the URL param used to track retry attempts (default `'mr'`). */
  retryParam?: string;
  /** Override the max retry count before falling through (default `3`). */
  maxRetries?: number;
};

/**
 * Returns an `onUnauthorized` callback for `Auth.Boundary` that retries the
 * MFA step-up redirect when the user returned from login but still needs
 * initial MFA. `Auth.Boundary`'s `ap=x` loop guard would otherwise short-
 * circuit the redirect; this helper bumps `mr=<n>` (clearing `ap`) and
 * re-invokes `redirectHref` up to `maxRetries` times before falling through.
 *
 * Pair with `useClearMfaRetryParam` so a leftover `mr=<maxRetries>` from a
 * prior session does not short-circuit the next MFA cycle.
 */
export function buildMfaStepUpRetryHandler<U = unknown>({
  appName,
  buildNumber = 0,
  user,
  redirectHref,
  fallback,
  redirecting,
  retryParam = DEFAULT_MFA_RETRY_PARAM,
  maxRetries = DEFAULT_MAX_MFA_RETRIES,
}: MfaRetryHandlerOptions<U>): (unauthorizedUser: U) => ReactNode {
  const resolveFallback = (unauthorizedUser: U) =>
    typeof fallback === 'function' ? fallback(unauthorizedUser) : fallback;

  return (unauthorizedUser: U) => {
    if (typeof window === 'undefined' || !user?.isInitialMultiFactorAuthenticationRequired) {
      return resolveFallback(unauthorizedUser);
    }

    const params = new URLSearchParams(window.location.search);
    const attempt = Number(params.get(retryParam) ?? '0');
    if (attempt >= maxRetries) {
      logError({
        error: new Error('MFA step-up retry limit reached'),
        errorType: ERROR_TYPE_MFA_STEP_UP_RETRY_EXHAUSTED,
        context: {
          appName,
          buildNumber,
          attempt,
          maxRetries,
          retryParam,
          zgcus_aeut: getCookie('zgcus_aeut'),
          zgcus_aeuut: getCookie('zgcus_aeuut'),
          zg_anonymous_id: getCookie('zg_anonymous_id'),
          zguid: getCookie('zguid'),
        },
      });

      return resolveFallback(unauthorizedUser);
    }

    params.set(retryParam, String(attempt + 1));
    params.delete('ap');

    getGlobalMonitor()?.count(
      { name: 'mfa step-up attempt total' },
      { name: `mfa step-up attempt ${attempt}` },
      { name: `mfa step-up attempt type ${ERROR_TYPE_MFA_STEP_UP_ATTEMPT}` },
    );

    window.location.href = redirectHref(window.location.href.split('?')[0], params);
    return redirecting;
  };
}

/**
 * Strips the MFA retry counter from the URL once the user is fully logged in.
 * Without this, a leftover `mr=<maxRetries>` param from an earlier session
 * would short-circuit the next MFA step-up cycle straight to retry-exhausted
 * before any redirect is attempted.
 *
 * Auth states:
 * - Logged out:        loggedIn=false, isInitialMultiFactorAuthenticationRequired=false
 * - Partially logged:  loggedIn=false, isInitialMultiFactorAuthenticationRequired=true
 * - Fully logged in:   loggedIn=true,  isInitialMultiFactorAuthenticationRequired=false
 *
 * Gated on `loggedIn`, not `!isInitialMultiFactorAuthenticationRequired`:
 * the latter matches both fully logged-in and fully logged-out, which would
 * clear the retry counter outside the success path.
 */
export function useClearMfaRetryParam({
  user,
  retryParam = DEFAULT_MFA_RETRY_PARAM,
}: {
  user?: { loggedIn?: boolean } | null;
  retryParam?: string;
}) {
  const loggedIn = user?.loggedIn;

  useEffect(() => {
    if (typeof window === 'undefined' || !loggedIn) {
      return;
    }

    const url = new URL(window.location.href);
    if (!url.searchParams.has(retryParam)) {
      return;
    }

    const attempt = Number(url.searchParams.get(retryParam) ?? '0');
    getGlobalMonitor()?.count(
      { name: 'mfa success total' },
      { name: `mfa success attempt ${attempt}` },
      { name: `mfa success type ${SUCCESS_TYPE_MFA_STEP_UP}` },
    );

    url.searchParams.delete(retryParam);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }, [loggedIn, retryParam]);
}
